import { CSSProperties, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSignUpContext } from "../../../../context/SignUpContext";
import { useContentfulContext } from "../../../../context/ContentfulContext";

/**
 * Development-only jump list for the signup flow.
 *
 * The first step calls an endpoint requiring Cloudflare attestation headers,
 * which are not issued against a local origin — so locally the flow cannot be
 * walked past the email step. This opens any later step directly.
 *
 * Navigating alone is not enough: the steps guard themselves two ways, and
 * both have to be satisfied or they redirect straight back to step 1.
 *
 *   1. Most steps bail when `userInfo.email` is empty — and the signup context
 *      starts every field as `""`, so the guard fires immediately.
 *   2. The later steps bail when `signUpStep` is lower than their own number.
 *
 * So each entry seeds a placeholder email and sets the step counter before
 * navigating. The email is only ever held in React state for this session; it
 * is never submitted anywhere by jumping.
 *
 * Compiled out of production builds: `react-scripts build` sets
 * `NODE_ENV=production`, the guard below folds to a constant and the component
 * is dropped by dead-code elimination. Styles are inline rather than in a
 * stylesheet because SCSS imports are *not* tree-shaken.
 */
const DEV_EMAIL = "dev.preview@example.com";

/** Mirrors the step numbers in `SignUpV2`; kept local to avoid an import cycle. */
const STEPS: { label: string; to: string; step: number; contentGated?: boolean }[] = [
  { label: "1. Email", to: "/signup", step: 0 },
  { label: "2. Phone", to: "/signup/phone", step: 1 },
  { label: "3. Verify phone", to: "/signup/smsverification", step: 2 },
  { label: "4. Password", to: "/signup/password", step: 3 },
  { label: "5. Plans", to: "/signup/subscription", step: 4 },
  { label: "6. Legal name", to: "/signup/legal", step: 5 },
  { label: "7. Referrals", to: "/signup/referrals", step: 6 },
  { label: "8. About your data", to: "/signup/aboutyourdata", step: 7 },
  { label: "9. Important info", to: "/signup/important-information", step: 8, contentGated: true },
  { label: "10. Payment", to: "/signup/payment", step: 9 },
  { label: "11. Payment date", to: "/signup/paymentdate", step: 10 },
  { label: "12. Confirmation", to: "/signup/confirmation", step: 11 },
  { label: "13. Schedule", to: "/signup/schedule", step: 12 },
];

const styles: Record<string, CSSProperties> = {
  root: {
    position: "fixed",
    right: 18,
    bottom: 18,
    zIndex: 2000,
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "flex-end",
    gap: 8,
    fontFamily: "Inter, system-ui, sans-serif",
  },
  toggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 38,
    padding: "0 14px",
    border: "1px solid rgba(255,255,255,0.14)",
    borderRadius: 999,
    background: "#0A0A0A",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 24px 48px -20px rgba(10,10,10,0.4)",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: 236,
    maxHeight: "60vh",
    overflowY: "auto",
    padding: 10,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "#0A0A0A",
    boxShadow: "0 24px 48px -20px rgba(10,10,10,0.4)",
  },
  note: {
    margin: "0 0 8px",
    padding: "0 6px",
    fontSize: 11,
    lineHeight: 1.4,
    color: "rgba(255,255,255,0.55)",
  },
  item: {
    padding: "7px 10px",
    borderRadius: 8,
    border: 0,
    background: "transparent",
    color: "rgba(255,255,255,0.75)",
    fontSize: 13,
    textAlign: "left",
    cursor: "pointer",
  },
  itemActive: {
    padding: "7px 10px",
    borderRadius: 8,
    border: 0,
    background: "#D4A24A",
    color: "#0A0A0A",
    fontSize: 13,
    fontWeight: 600,
    textAlign: "left",
    cursor: "pointer",
  },
};

export const SignupDevNav = ({ setSignUpStep }: { setSignUpStep: (step: number) => void }) => {
  const [open, setOpen] = useState<boolean>(false);
  const { pathname } = useLocation();
  const { userInfo, setUserInfo } = useSignUpContext();
  const { content } = useContentfulContext();
  const { displayImportantInformationPage }: any = content ?? { displayImportantInformationPage: false };
  const navigate = useNavigate();

  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  const jumpTo = (target: { to: string; step: number }) => {
    if (!userInfo?.email?.trim()) {
      setUserInfo({ ...userInfo, email: DEV_EMAIL });
    }
    setSignUpStep(target.step);
    navigate(target.to);
  };

  return (
    <div style={styles.root}>
      <button type="button" style={styles.toggle} onClick={() => setOpen((value) => !value)}>
        <span>Signup steps</span>
        <span>{open ? "▾" : "▴"}</span>
      </button>

      {open && (
        <nav style={styles.list}>
          <p style={styles.note}>
            Dev only. Fills a placeholder email and step number so the step&rsquo;s guards pass.
          </p>
          {/* The important-information step is only part of the flow when
              Contentful enables it; jumping to it otherwise lands on a page
              the real flow never reaches. */}
          {STEPS.filter((step) => !step.contentGated || displayImportantInformationPage).map((step) => (
            <button
              type="button"
              key={step.to}
              style={pathname === step.to ? styles.itemActive : styles.item}
              onClick={() => jumpTo(step)}
            >
              {step.label}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
};
